import type { GlobalClusterDefinition, ResolvedCurrentCluster } from '~/utils/clusters';
import type { GeoRegion } from '~/utils/geo';

import { CountryCode } from '~/graphql/generated/graphql';
import { useConfigStore } from '~/stores/config';
import { EMPTY_CLUSTER, filterClustersByProduct, parseGlobalClusterDefinitions } from '~/utils/clusters';

const toGeoRegion = (code: string | undefined): GeoRegion | null => {
  if (code === CountryCode.Usa) {
    return 'USA';
  }
  if (code === CountryCode.Aus) {
    return 'AUS';
  }
  return null;
};

/**
 * Single source of truth for the public global cluster list. Parsed once per SSR
 * request (then hydrated to the client) and shared with every consumer (the SEO
 * plugin and the region switcher). A malformed value is a deploy misconfig — log
 * and degrade to no clusters rather than break the page (cosmetic feature).
 *
 * `clusters` is the same-product subset: the geo switcher and hreflang only ever
 * pair a cluster with its sibling product cluster (consumer ↔ consumer,
 * schools ↔ schools), keyed off the current cluster's code.
 */
export const useGlobalClusters = () => {
  const config = useRuntimeConfig();
  const configStore = useConfigStore();
  const { $logErrorMessage } = useNuxtApp();

  const allClusters = useState<GlobalClusterDefinition[]>('global-clusters', () => {
    try {
      return parseGlobalClusterDefinitions(config.public.globalClusterDefinitions);
    }
    catch (err) {
      $logErrorMessage('[useGlobalClusters] invalid GLOBAL_CLUSTER_DEFINITIONS', { error: err });
      return [];
    }
  });

  // The current cluster's code, resolved on SSR from X-Cluster-Code
  // (router.options.ts) — same shared state useCurrentCluster reads.
  const clusterState = useState<ResolvedCurrentCluster>('cluster', () => EMPTY_CLUSTER);

  const clusters = computed<GlobalClusterDefinition[]>(() =>
    filterClustersByProduct(allClusters.value, clusterState.value.code),
  );

  // The cluster this deployment serves, matched by its country within the
  // same-product subset (geo alone is ambiguous once schools clusters exist).
  const currentCluster = computed<GlobalClusterDefinition | null>(() => {
    const geo = toGeoRegion(configStore.publicConfig?.country?.code);
    return geo ? (clusters.value.find(cluster => cluster.geo === geo) ?? null) : null;
  });

  return { clusters, currentCluster };
};
